import { HealthcareFacilityPart, ServiceProvidingLocation } from '@dreryk/egabinet-healthcare-provider-frontend-api';
import {
   ServiceLocationId,
   ServiceLocationIdValue,
   ServiceLocationType,
} from '@dreryk/egabinet-healthcare-provider-published-language-api';

import { assertUnreachable } from '@dreryk/react-common';

import { HealthcareProviderLocation, HealthcareProviderLocationIdentifier } from './healthcareProviderLocation.types';

const isHealthcareFacilityPart = (
   location: ServiceProvidingLocation | HealthcareFacilityPart
): location is HealthcareFacilityPart => {
   return 'healthcareFacilityUnit' in location || 'healthcareFacilityLocation' in location;
};

export const toHealthcareProviderLocation = (
   locationRaw: ServiceProvidingLocation | HealthcareFacilityPart
): HealthcareProviderLocation => {
   if (isHealthcareFacilityPart(locationRaw)) {
      const location = locationRaw.healthcareFacilityLocation || locationRaw.healthcareFacilityUnit;
      const identifierType = locationRaw.healthcareFacilityLocation
         ? ServiceLocationType.HEALTHCARE_FACILITY_LOCATION
         : ServiceLocationType.HEALTHCARE_FACILITY_UNIT;

      return {
         name: location.name,
         address: location.address,
         identifier: {
            type: identifierType,
            id: location.id,
         },
         id: ServiceLocationId.of(identifierType, location.id),
      };
   }

   return {
      name: locationRaw.name,
      address: locationRaw.address,
      identifier: {
         type: ServiceLocationType.SERVICE_PROVIDING_LOCATION,
         id: locationRaw.id,
      },
      id: ServiceLocationId.of(ServiceLocationType.SERVICE_PROVIDING_LOCATION, locationRaw.id),
   };
};

export const toHealthcareProviderIdentifier = (
   serviceLocationIdValue: ServiceLocationIdValue
): HealthcareProviderLocationIdentifier => {
   const [identifierType, identifierValue] = serviceLocationIdValue.split(':');

   const parsedValue = Number(identifierValue);

   if (Number.isNaN(parsedValue)) {
      throw new Error('Invalid value provided as healthcareProviderService id');
   }

   return {
      type: identifierType as ServiceLocationType,
      id: parsedValue,
   };
};

type SelectorKeyMap = {
   [ServiceLocationType.SERVICE_PROVIDING_LOCATION]: 'selectServiceProvidingLocation';
   [ServiceLocationType.HEALTHCARE_FACILITY_LOCATION]: 'selectHealthcareFacilityLocation';
   [ServiceLocationType.HEALTHCARE_FACILITY_UNIT]: 'selectHealthcareFacilityUnit';
};

type HealthcareProviderLocationSelectors = Partial<{
   selectServiceProvidingLocation: () => any;
   selectHealthcareFacilityLocation: () => any;
   selectHealthcareFacilityUnit: () => any;
}>;

type SelectorReturn<S, T extends ServiceLocationType> = SelectorKeyMap[T] extends keyof S
   ? S[SelectorKeyMap[T]] extends (...args: any) => infer R
      ? R
      : never
   : never;

export const healthcareProviderLocationSelector = <
   TYPE extends ServiceLocationType
>(healthcareProviderLocationIdentifier: {
   type: TYPE;
}) => <SELECTORS extends HealthcareProviderLocationSelectors>(
   selectors: SELECTORS
): SelectorReturn<SELECTORS, TYPE> => {
   switch (healthcareProviderLocationIdentifier.type) {
      case ServiceLocationType.SERVICE_PROVIDING_LOCATION:
         return selectors.selectServiceProvidingLocation?.();
      case ServiceLocationType.HEALTHCARE_FACILITY_LOCATION:
         return selectors.selectHealthcareFacilityLocation?.();
      case ServiceLocationType.HEALTHCARE_FACILITY_UNIT:
         return selectors.selectHealthcareFacilityUnit?.();
      default:
         return assertUnreachable(healthcareProviderLocationIdentifier.type);
   }
};
